import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardFooter } from 'reactstrap';

import CopyButton from '../../CopyButton/CopyButton';
import EventTitle from '../../EventTitle';
import PosterListCell from '../../PosterListCell';
import { POSTER_LIST_VARIANT_SCREENINGS } from '../../../config/posterListVariant';
import { pick_poster } from '../../../services/eventPoster';
import { build_eventival_backoffice_url } from '../../../services/eventivalUrls';
import { screeningEshopBuyUrl } from '../../../services/screeningEshopUrl';
import { Screenings } from '../../../services';
import { group_vod_screenings } from '../vodLinkedScreenings';

import './VodLinkedScreenings.scss';

const LIST_FIELDS = [
    '_id',
    'title',
    'start_at',
    'opaque.shift72.media_id',
    'opaque.posters',
    'opaque.eventival.type',
    'refs',
    'films._id',
    'films.opaque.eventival.type',
];

const screening_when = (screening) => {
    if (!screening?.start_at)
        return '';

    const value = moment.isMoment(screening.start_at)
        ? screening.start_at
        : moment(screening.start_at);

    return value.isValid() ? value.format('ll LT') : '';
};

const event_titles = (title) => {
    if (title && typeof title === 'object' && !Array.isArray(title))
        return title;
    if (typeof title === 'string' && title.trim())
        return { original: title.trim() };
    return null;
};

const title_key = (screening) => JSON.stringify(event_titles(screening?.title));

/**
 * Poster and Eventival link are shared by the screenings of one film.
 * A different poster or Eventival URL becomes its own group.
 */
const presentation_cluster = (screening, setting) => {
    const poster = pick_poster(screening?.opaque?.posters);
    const poster_key = poster?.url || '';
    const eventival_key = build_eventival_backoffice_url(screening, setting) || '';
    return `${poster_key}\n${eventival_key}`;
};

const VodGroupCard = ({ group, setting }) => {
    const { t } = useTranslation();
    const sample = group.screenings[0];
    const poster = pick_poster(sample?.opaque?.posters);
    const titles = event_titles(sample?.title);
    const shared_title = group.screenings.every(row => title_key(row) === title_key(sample));
    const eshop_uri = setting?.eshop?.uri;
    const eventival_url = build_eventival_backoffice_url(sample, setting);

    return (
        <Card className="VodLinkedScreenings__item">
            <CardBody className="p-0">
                <PosterListCell
                    poster={poster}
                    posterUrl={poster?.url}
                    variant={POSTER_LIST_VARIANT_SCREENINGS}
                    fallbackIcon="television"
                >
                    {shared_title && titles && <EventTitle plain textSize={14} titles={titles} />}
                    <div className="VodLinkedScreenings__head">
                        <CopyButton
                            badge
                            size="sm"
                            color="secondary"
                            className="text-small"
                            textToCopy={group.media_id}
                        />
                        <span className="text-muted">
                            { t('programmations.x_screenings', { count: group.screenings.length }) }
                        </span>
                    </div>
                </PosterListCell>
                <ul className="VodLinkedScreenings__sessions">
                    {group.screenings.map(screening => {
                        const when = screening_when(screening);
                        const row_titles = event_titles(screening?.title);
                        return (
                            <li className="VodLinkedScreenings__session" key={screening._id}>
                                {!shared_title && row_titles && (
                                    <EventTitle plain textSize={14} titles={row_titles} />
                                )}
                                <Link to={`/screenings/edit/${screening._id}`}>
                                    {when || t('common.screening')}
                                </Link>
                                {eshop_uri && (
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-info"
                                        href={screeningEshopBuyUrl(eshop_uri, screening)}
                                    >
                                        <i className="fa fa-external-link-square"></i>&nbsp;
                                        { t('screenings.see_on_eshop') }
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </CardBody>
            {eventival_url && (
                <CardFooter className="VodLinkedScreenings__actions">
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-info"
                        href={eventival_url}
                    >
                        <i className="fa fa-external-link-square"></i>&nbsp;
                        { t('screenings.see_on_eventival') }
                    </a>
                </CardFooter>
            )}
        </Card>
    );
};

const VodLinkedScreenings = ({ mediaIdOf, provider }) => {
    const { t } = useTranslation();
    const edition_id = useSelector(state => state.editionNavigation?.selectedEditionId);
    const setting = useSelector(state => state.settings?.setting);
    const [screenings, setScreenings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);

        const params = { fields: LIST_FIELDS };
        if (!edition_id)
            params.stop_at_gte = new Date().toISOString();

        Screenings.list(params)
            .then((data) => {
                if (!cancelled)
                    setScreenings(data?.screenings || []);
            })
            .catch(() => {
                if (!cancelled) {
                    setScreenings([]);
                    setFailed(true);
                }
            })
            .finally(() => {
                if (!cancelled)
                    setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [edition_id]);

    const grouped = useMemo(
        () => group_vod_screenings(
            screenings,
            mediaIdOf,
            (screening) => presentation_cluster(screening, setting)
        ),
        [screenings, mediaIdOf, setting]
    );

    return (
        <Card className="VodLinkedScreenings h-auto mt-3">
            <CardBody>
                <h3 className="VodLinkedScreenings__title">
                    { t('settings.integrations.vod_linked.title', { provider }) }
                </h3>
                {loading && <p className="mb-0">{ t('common.loading') }</p>}
                {!loading && failed && <p className="mb-0 text-danger">{ t('settings.integrations.vod_linked.load_error') }</p>}
                {!loading && !failed && (
                    <>
                        <div className="VodLinkedScreenings__summary">
                            <span>{ t('settings.integrations.vod_linked.with_id', {
                                withId: grouped.with_id,
                                total: grouped.total,
                            }) }</span>
                        </div>
                        {grouped.groups.length === 0 && (
                            <p className="mb-0 text-muted">{ t('settings.integrations.vod_linked.none') }</p>
                        )}
                        <div className="VodLinkedScreenings__groups">
                            {grouped.groups.map(group => (
                                <VodGroupCard
                                    key={`${group.media_id}\u0000${group.cluster}`}
                                    group={group}
                                    setting={setting}
                                />
                            ))}
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default VodLinkedScreenings;
